'use client';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Header } from '@/ui-components/organisms/Header/Header';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Button } from '@/ui-components/atoms/Button/Button';
import submitJson from '@/ui-components/atoms/Inputs/Form/contentTypes/json';
import Ended from './SweepsEnded';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { mapChannelCard } from '@/components/molecules/ChannelCard/ChannelDataUtils';
import ChannelCardGroupWrapper from '@/components/molecules/ChannelCardGroup/ChannelCardGroupWrapper';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { ensureDeviceGrant } from '@/services/microservices/deviceGrant';
import { FlexibleFormBase, FlexibleFormVariant } from './FlexibleFormBase';
import {
    getSmsOptInCheckboxAdditionalProps,
    getSmsOptInCheckboxClickHandler,
    parseFormSubmitResponse,
    SMS_OPT_IN_FIELD_NAME,
} from './flexibleFormShared';
import styles from './flexibleForm.module.scss';

const {
    leftAlignedHeaderStyles,
    descriptionTextStyles,
    flexForm,
    halfWidth,
    fullWidth,
    formSectionHeaderStyles,
    formSectionTextBlockStyles,
    dropdownSelectStyles,
    submitButtonWrapperCentered,
    submitButtonWrapperRight,
    successHeadlineStyles,
    successErrorWrapper,
    channelCardsWrapper,
    wrapperRow,
    stateContainer,
    stateWrapper,
    zipInputStyles,
    subheadlineStyles,
    ctaStyles,
    duplicationFormError,
    duplicationFormErrorCentered,
} = styles;

interface ClientFlexibleFormProps {
    asset: SiteFlexibleForm;
}

export function SweepsFlexibleForm({ asset }: ClientFlexibleFormProps) {
    const {
        fields: {
            headline,
            description,
            presentation,
            sweeps,
            error_headline,
            error_subheadline,
            success_headline,
            success_subheadline,
            error_cta,
            success_page_redirect,
        },
    } = asset;
    const router = useRouter();
    const [ended, setEnded] = useState(false);
    const [deviceId, setDeviceId] = useState('');
    const [submitSucceeded, setSubmitSucceeded] = useState(false);
    const presentationStyles = presentation?.styles;
    const isCenter = presentationStyles?.alignment === 'Centre';
    const needsDeviceId = asset.fields.form_fields.some(
        (formField) => formField.fields.field_name === SMS_OPT_IN_FIELD_NAME,
    );

    useEffect(() => {
        if (!needsDeviceId) {
            return;
        }

        let cancelled = false;
        ensureDeviceGrant().then((grant) => {
            if (!cancelled && grant) {
                setDeviceId(grant.deviceId);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [needsDeviceId]);

    useEffect(() => {
        if (submitSucceeded && success_page_redirect) {
            router.push(success_page_redirect.fields.href);
        }
    }, [submitSucceeded, success_page_redirect, router]);

    const deviceIdHiddenInput = needsDeviceId ? (
        <input type="hidden" name="deviceId" value={deviceId} data-cy="sweeps-form-hidden-device-id" />
    ) : null;

    const sweepsFields = sweeps?.fields;
    if (ended && sweepsFields) {
        return (
            <Ended
                sweepsFields={{
                    closed_headline: sweepsFields.closed_headline,
                    closed_description: sweepsFields.closed_description,
                    closed_message: sweepsFields.closed_message,
                }}
                asW2
            />
        );
    }

    const sweepsFlexibleFormVariant: FlexibleFormVariant = {
        formType: 'sweeps',
        styles: {
            flexForm,
            halfWidth,
            fullWidth,
            formSectionHeaderStyles,
            formSectionTextBlockStyles,
            dropdownSelectStyles,
            wrapperRow,
            stateContainer,
            stateWrapper,
            zipInputStyles,
        },
        analytics: {
            successEvent: 'sweeps-success',
            failEvent: 'sweeps-fail',
        },
        getCheckboxAdditionalProps: getSmsOptInCheckboxAdditionalProps,
        getCheckboxClickHandler: getSmsOptInCheckboxClickHandler,
        sourceName: 'DE_Sweepstakes',
        trackSubmitAttempted: true,
        passCheckboxRequired: true,
        extraDropdownTypes: ['nfl favorite team'],
        submitButtonFitting: 'hug-contents',
        getStateSelectHeader: (fieldLabel) => ({
            title: fieldLabel,
            body: '',
        }),
        getDropdownSelectHeader: (fieldLabel) => ({
            title: fieldLabel,
            body: '',
        }),
        onValidSubmit: (data) => {
            if (needsDeviceId) {
                const smsOptedIn = data.getAll(SMS_OPT_IN_FIELD_NAME).includes('Y');
                data.delete(SMS_OPT_IN_FIELD_NAME);
                data.set(SMS_OPT_IN_FIELD_NAME, smsOptedIn ? 'Y' : 'N');
            }

            if (!data.get('nfl_optin')) {
                return;
            }

            const nflFormData = new FormData();
            nflFormData.set('languagePreference', 'en-US');
            nflFormData.set('sourceName', 'DE_Sweepstakes');
            nflFormData.set('email', (data.get('email') as string) ?? '');
            nflFormData.set('firstName', (data.get('firstName') as string) ?? '');
            nflFormData.set('lastName', (data.get('lastName') as string) ?? '');
            nflFormData.set('streetAddress', (data.get('address1') as string) ?? '');
            nflFormData.set('city', (data.get('city') as string) ?? '');
            nflFormData.set('state', (data.get('state') as string) ?? '');
            nflFormData.set('zip', (data.get('zip') as string) ?? '');
            nflFormData.set('favoriteTeam', (data.get('favoriteTeam') as string) ?? '');

            submitJson('/phx/services/slam/utility/nfl-opt-in', 'POST', nflFormData);
        },
        onSuccessResponse: async (response, { validations, setValidations, setSubmitState }) => {
            const json = await response.json();
            const parsedResponse = parseFormSubmitResponse(json, validations, { sweepstakesEnded: true });

            if (parsedResponse.submitState) {
                setSubmitState(parsedResponse.submitState);
            }

            if (parsedResponse.sweepstakesEnded) {
                setEnded(true);
            }

            if (Object.keys(parsedResponse.serverValidations).length > 0) {
                setValidations({ ...validations, ...parsedResponse.serverValidations });
            }

            if (parsedResponse.submitState === 'success') {
                setSubmitSucceeded(true);
            }
        },
        renderContent: ({ wrappedFieldElements, duplicateError, duplicateErrorMessage, submitButton, errorDisplay }) =>
            isCenter ? (
                <>
                    <StyledContainer standardMoleculeSpacing>
                        <Header
                            headingTag="h2"
                            heading={<RenderRTE contentfulDoc={headline} />}
                            subhead={<RenderRTE contentfulDoc={description} />}
                        />
                    </StyledContainer>
                    <Grid container>
                        <Grid col={{ sm: 12, md: 6, lg: 6 }} start={{ md: 4, lg: 4 }}>
                            {errorDisplay}
                        </Grid>
                        <Grid col={{ sm: 12, md: 6, lg: 6 }} start={{ md: 4, lg: 4 }}>
                            <div>
                                {deviceIdHiddenInput}
                                <div className={flexForm}>{wrappedFieldElements}</div>
                                <div className={submitButtonWrapperCentered}>
                                    {duplicateError && (
                                        <StyledContainer>
                                            <Text tag="div" className={duplicationFormErrorCentered} role="alert">
                                                {duplicateErrorMessage}
                                            </Text>
                                        </StyledContainer>
                                    )}
                                    {submitButton}
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </>
            ) : (
                <Grid container>
                    <Grid col={{ sm: 12, md: 6 }} standardMoleculeSpacing>
                        <Header
                            headingTag="h2"
                            heading={<RenderRTE contentfulDoc={headline} />}
                            theme="left"
                            className={leftAlignedHeaderStyles}
                        />
                    </Grid>
                    <Grid col={{ sm: 12, md: 6 }}>
                        <Text tag="p" className={descriptionTextStyles}>
                            <RenderRTE contentfulDoc={description} />
                        </Text>
                        <Grid col={{ sm: 12, lg: 6 }}>
                            {deviceIdHiddenInput}
                            <div className={flexForm}>{wrappedFieldElements}</div>
                        </Grid>
                        <div className={submitButtonWrapperRight}>
                            {duplicateError && (
                                <StyledContainer>
                                    <Text tag="div" className={duplicationFormError} role="alert">
                                        {duplicateErrorMessage}
                                    </Text>
                                </StyledContainer>
                            )}
                            {submitButton}
                        </div>
                    </Grid>
                </Grid>
            ),
        renderSubmitMessage: ({ submitState }) => {
            const success = submitState === 'success';
            const failed = submitState === 'failed';

            let featuredChannelsData = null;
            if (success && sweeps?.fields.featured_channels) {
                const featuredChannels = mapChannelCard(sweeps.fields.featured_channels);
                const [channelIds, channelCards] = featuredChannels;
                featuredChannelsData = channelCards?.length > 0 && (
                    <div className={channelCardsWrapper}>
                        <ChannelCardGroupWrapper
                            channels={channelCards}
                            channelIds={channelIds}
                            title={sweeps.fields.channel_card_intro_text}
                            link={sweeps.fields.channel_group_link}
                        />
                    </div>
                );
            }

            const errorCTAFields = error_cta?.fields;

            return (
                <div className={successErrorWrapper}>
                    {success && (
                        <ImageWrapper
                            src="https://www.siriusxm.com/content/dam/sxm-com/lottie/stella/Stella-Wag-Animation.gif"
                            alt="Stella Alert"
                            optimizedWidth={100}
                            optimizedHeight={100}
                            loadingStrategy="eager"
                            skipOptimization={true}
                        />
                    )}
                    <div data-cy="sweeps-form-submit-success-message" className={successHeadlineStyles}>
                        <Text tag="h5" variant="h5">
                            {success && success_headline}
                            {failed && error_headline}
                        </Text>
                    </div>
                    <Text tag="p" className={subheadlineStyles}>
                        {success && <RenderRTE contentfulDoc={success_subheadline} />}
                        {failed && <RenderRTE contentfulDoc={error_subheadline} />}
                    </Text>
                    {failed && errorCTAFields && (
                        <Button
                            type="button"
                            className={ctaStyles}
                            href={errorCTAFields.href}
                            linkTarget={errorCTAFields.link_target}
                            ariaLabel={errorCTAFields.name}
                            variant={errorCTAFields.theme}
                            size={errorCTAFields.size.toLowerCase()}
                            fitting="hug-contents"
                            dataCy="sweeps-form-error-cta"
                        >
                            {errorCTAFields.link_text}
                        </Button>
                    )}
                    {featuredChannelsData}
                </div>
            );
        },
        renderShell: ({ submitState, submitMessage, form }) => (
            <>
                <div aria-live="polite">{submitMessage}</div>
                {submitState === 'untouched' || submitState === 'duplicate' ? form : undefined}
            </>
        ),
    };

    return <FlexibleFormBase asset={asset} variant={sweepsFlexibleFormVariant} />;
}
